import { useMemo, useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import SearchInput from "common/components/SearchInput";
import {
  DropdownMenuContent,
  DropdownMenuList,
} from "common/components/ui/DropdownMenu";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { GenericSearchParam } from "common/types";

import { PROFILE_SEARCH_OPTIONS } from "../../../../../../constants";
import useSearchOptions from "../../../../../../datahooks/useSearchOptions";
import { useSearchParamsContext } from "../../SearchParamsContext";
import useSearchParams from "../useSearchParams";
import SearchParamOption from "./SearchParamOption";

interface LanguagesProps {
  optionsKey: "languages";
}

export default function Languages({ optionsKey }: LanguagesProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [inputValue, setInputValue] = useState("");
  const { searchParams } = useSearchParamsContext();

  const {
    addSearchParamValue,
    removeSearchParamValue,
    appliedSearchParamValues,
    setSearchParamValue,
  } = useSearchParams(optionsKey);

  const { suggestions } = useSearchOptions({
    searchParams,
  });

  const languagesSuggestions: GenericSearchParam[] = suggestions
    ? suggestions[optionsKey]
    : [];

  const { label } = PROFILE_SEARCH_OPTIONS[optionsKey];

  const lowercasedInput = inputValue.toLowerCase();

  const sortedAndFilteredOptions = useMemo(() => {
    const existingUrns = new Set(
      appliedSearchParamValues.map(({ urn }) => urn),
    );

    const uniqueResults = languagesSuggestions.filter(
      (option) => !existingUrns.has(option.urn),
    );

    const mergedOptions = [
      ...(appliedSearchParamValues as GenericSearchParam[]),
      ...uniqueResults,
    ];

    const filteredOptions = mergedOptions.filter(({ urn, name }) => {
      if (existingUrns.has(urn)) {
        return true;
      }

      return name.toLowerCase().includes(lowercasedInput);
    });

    return filteredOptions;
  }, [appliedSearchParamValues, languagesSuggestions, lowercasedInput]);

  const listContent = sortedAndFilteredOptions.length ? (
    sortedAndFilteredOptions.map((option) => {
      const isSelected = !!appliedSearchParamValues.find(
        ({ urn }) => urn === option.urn,
      );

      function onToggle() {
        if (isSelected) {
          removeSearchParamValue(optionsKey, option);
        } else {
          addSearchParamValue(optionsKey, option);
        }
      }

      return (
        <SearchParamOption
          key={option.urn}
          isSelected={isSelected}
          onSelect={() => setSearchParamValue(optionsKey, [option])}
          onToggle={onToggle}
          option={option}
        />
      );
    })
  ) : (
    <p className="px-4 py-8 text-center text-black-500">No results found</p>
  );
  return isDesktop ? (
    <DropdownMenuContent className="w-80" align="start">
      <MenuSearchInput
        value={inputValue}
        placeholder={`Search ${label}`}
        onChange={(e) => setInputValue(e.currentTarget.value)}
      />
      <DropdownMenuList>{listContent}</DropdownMenuList>
    </DropdownMenuContent>
  ) : (
    <>
      <SearchInput
        value={inputValue}
        placeholder={`Search ${label}`}
        onChange={(e) => setInputValue(e.currentTarget.value)}
        onClear={() => setInputValue("")}
        className="mb-2"
      />
      <div className="flex flex-col">{listContent}</div>
    </>
  );
}
